import React, { useContext } from 'react';
import {
    IRisk, IRiskUpdate, IDirectorate, Contributor, RiskRiskType, IEntity, IProject,
    EntityValidations, ISpecificEntityFormProps, CustomField, ICorporateRisk, CorporateRisk,
    SaveStatus
} from '../../types';
import { DateService, LookupService, ReportingCycleService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrDropdown } from '../cr/CrDropdown';
import { CrTextField } from '../cr/CrTextField';
import { CrChoiceGroup } from '../cr/CrChoiceGroup';
import { EntityStatus } from '../../refData/EntityStatus';
import { CrDatePicker } from '../cr/CrDatePicker';
import { RiskRegister } from '../../refData/RiskRegister';
import { CrCheckbox } from '../cr/CrCheckbox';
import { CrComboBox } from '../cr/CrComboBox';
import { CrEntityPicker } from '../cr/CrEntityPicker';
import { EntityForm } from '../EntityForm';
import { IEntityFormChangeHandlers } from '../../types/EntityFormProps';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { DataContext } from '../DataContext';
import { OrbUserContext } from '../OrbUserContext';
import { IUseUserContextProps } from '../useUserContext';
import { IUseDataContextProps } from '../useDataContext';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { CrCommandBar } from '../cr/CrCommandBar';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import useMobileDetector from '../useMobileDetector';
import EvidenceList from './EV/EvidenceList';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';

// Add wrapper to enable use of multiple contexts in class component
export const RiskForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const isMobile = useMobileDetector();
    const { userContext, userPermissions, orbConfig } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;

    return (
        <OrbUserContext.Consumer>
            {orbUserContext => (
                <DataContext.Consumer>
                    {dataContext => (
                        <RiskFormInner
                            isMobile={isMobile}
                            orbUserContext={orbUserContext}
                            dataContext={dataContext}
                            currentUserId={currentUserId}
                            superUserPermission={userPermissions.UserIsSystemAdmin()}
                            {...props}
                        />
                    )}
                </DataContext.Consumer>
            )}
        </OrbUserContext.Consumer>
    );
};


export class RiskValidations extends EntityValidations {
    public RiskRegisterID: string;
    public RiskOwnerUserID: string;
    public DirectorateID: string;
    public ProjectID: string;
    public RiskProximity: string;
    public NormalContributors: string = null;
    public ReportingCycle: string = null;
}

export interface IRiskFormInnerProps extends ISpecificEntityFormProps {
    orbUserContext: IUseUserContextProps;
    dataContext: IUseDataContextProps;
    isMobile: boolean;
    currentUserId: number;
    superUserPermission: boolean;
}

export interface IRiskFormState {
    LastSignedOffRiskUpdate: IRiskUpdate;
    FilteredDirectorates: IDirectorate[];
    //FilteredProjects: IProject[];
    Evidence_ListFilterText: string;
}

export class RiskFormState implements IRiskFormState {
    public LastSignedOffRiskUpdate = null;
    public FilteredDirectorates = [];
    //public FilteredProjects = [];
    public Evidence_ListFilterText = '';
}

class RiskFormInner extends React.Component<IRiskFormInnerProps, IRiskFormState> {

    constructor(props: IRiskFormInnerProps) {
        super(props);
        this.state = new RiskFormState();
    }

    public render(): React.ReactElement {
        const { orbUserContext: { userContext, orbConfig }, dataContext: { dataServices, lookupData }, isMobile } = this.props;

        const validateEntity = (risk: ICorporateRisk): Promise<RiskValidations> => {
            const errors = new RiskValidations();

            if (risk.Title === null || risk.Title === '') {
                errors.Title = 'Risk name is required';
                errors.Valid = false;
            }
            else {
                errors.Title = null;
            }

            if (risk.RiskRegisterID === null) {
                errors.RiskRegisterID = 'Register is required';
                errors.Valid = false;
            }
            else {
                errors.RiskRegisterID = null;
            }

            // if (risk.DirectorateID === null) {
            //     errors.DirectorateID = `${orbConfig.OrgL2Label} is required`;
            //     errors.Valid = false;
            // }
            // else {
            //     errors.DirectorateID = null;
            // }

            if (risk.IsProjectRisk && risk.ProjectID === null) {
                errors.ProjectID = 'Project is required';
                errors.Valid = false;
            }
            else {
                errors.ProjectID = null;
            }

            if (risk.RiskOwnerUserID === null) {
                errors.RiskOwnerUserID = 'Risk owner is required';
                errors.Valid = false;
            }
            else {
                errors.RiskOwnerUserID = null;
            }

            if (risk.ID === 0 && risk.RiskProximity !== null && risk.RiskProximity < new Date()) {
                errors.RiskProximity = 'Risk proximity cannot be in the past';
                errors.Valid = false;
            } else {
                errors.RiskProximity = null;
            }

            if (risk.Contributors.some(ct => ct.ContributorUserID === risk.RiskOwnerUserID)) {
                errors.NormalContributors = 'A user cannot be both the lead and a contributor';
                errors.Valid = false;
            }
            else {
                errors.NormalContributors = null;
            }

            if (!ReportingCycleService.reportingCycleIsValid(risk)) {
                errors.ReportingCycle = 'Please select all values for the reporting cycle';
                errors.Valid = false;
            }
            else {
                errors.ReportingCycle = null;
            }

            return Promise.resolve(errors);
        };


        const filteredCustomFieldDfns = getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'Risk Register - Common',
                'Risk Register - Risks',
            ]
        );

        const today = new Date();
        return (
            <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
                <EntityForm<ICorporateRisk, RiskValidations>
                    {...this.props}
                    mode='page'
                    entityName="Risk"
                    renderFormFields={(changeHandlers, formState, saveEntity) => {
                        const { FormData: risk, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;

                        let commandBarItems: ICommandBarItemProps[] = [];

                        if (FormSaveStatus === SaveStatus.Pending) {
                            commandBarItems.push({
                                key: 'Save',
                                text: 'Saving...',
                                iconProps: { iconName: 'Sync' },
                                disabled: true,
                            });
                        } else if (!FormIsDirty) {
                            commandBarItems.push({
                                key: 'Close',
                                text: 'Close',
                                iconProps: { iconName: 'Cancel' },
                                onClick: (ev, item) => {
                                    this.props.onCancelled();
                                },
                            });
                        } else {
                            commandBarItems.push(
                                {
                                    key: 'Save',
                                    text: 'Save',
                                    iconProps: { iconName: 'Save' },
                                    onClick: (ev, item) => {
                                        saveEntity();
                                    },
                                },
                                {
                                    key: 'Cancel',
                                    text: 'Cancel',
                                    iconProps: { iconName: 'Cancel' },
                                    onClick: (ev, item) => {
                                        this.props.onCancelled();
                                    },
                                }
                            );
                        }


                        //const escalationOptions = this.getRegisterOptions(risk, this.state.LastSignedOffRiskUpdate, lookupData.RiskRegisters, formState.FormDataBeforeChanges);
                        const mappedUsers = LookupService.riskUsers(lookupData, risk);
                        //console.log('mappedUsers', mappedUsers);
                        const suggestions = {
                            initialSuggestionsHeaderText:
                                risk.IsProjectRisk && risk.ProjectID ? `Project users` :
                                    risk.DirectorateID ? `Directorate and group users` : null,
                            initialSuggestions: mappedUsers,
                            noResultsFoundText:
                                mappedUsers.length === 0 ?
                                    risk.IsProjectRisk && risk.ProjectID ? `No users are mapped to this risk's project` :
                                        risk.DirectorateID ? `No users are mapped to this risk's directorate or group` : null : null
                        };
                        const nowPlusOneMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 0)); // AL: Not sure why this was implemented like this
                        if (nowPlusOneMonth instanceof Date)
                            console.log('nowPlusOneMonth is valid date', nowPlusOneMonth);
                        else
                            console.log('nowPlusOneMonth is not valid date', nowPlusOneMonth);

                        const unmitigatedProbabilityLabel = <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Unmitigated Probability</span></div>
                                <div className={styles.sectionQuestionCol2}>
                                    {/* <img src={reqCostCentreValidationImg} /> */}
                                </div>
                            </div>
                        </div>;

                        const unmitigatedImpactLabel = <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Unmitigated Impact</span></div>
                                <div className={styles.sectionQuestionCol2}>
                                    {/* <img src={reqDirectorateIdValidationImg} /> */}
                                </div>
                            </div>
                        </div>;

                        const targetProbabilityLabel = <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Target Probability</span></div>
                                <div className={styles.sectionQuestionCol2}>
                                    {/* <img src={reqDirectorateIdValidationImg} /> */}
                                </div>
                            </div>
                        </div>;

                        const targetImpactLabel = <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Target Impact</span></div>
                                <div className={styles.sectionQuestionCol2}>
                                    {/* <img src={reqDirectorateIdValidationImg} /> */}
                                </div>
                            </div>
                        </div>;

                        return (
                            <>
                                <CrCommandBar items={commandBarItems} />

                                {/* Basic Details Section */}
                                <div>
                                    <div style={{ marginBottom: '10px', marginTop: '10px' /*marginTop: '30px'*/ }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Basic Details</div>
                                        </div>
                                    </div>
                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Risk Name</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrTextField
                                                        maxLength={255}
                                                        value={risk.Title}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                                                        errorMessage={errors.Title}
                                                        required={true}
                                                        disabled={(this.isNameDisabled(risk)) || (risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2nd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Risk Type</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown
                                                        multiSelect
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.RiskTypes)}
                                                        selectedKeys={risk.RiskRiskTypes?.map(r => r.RiskTypeID)}
                                                        onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'RiskRiskTypes', new RiskRiskType(), 'RiskTypeID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 3rd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Date Risk Created</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDatePicker
                                                        value={DateService.removeTimezoneOffset(risk.RiskRegisteredDate)}
                                                        onSelectDate={date => changeHandlers.changeDatePicker(date, 'RiskRegisteredDate')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 4th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>What is the proximity of this risk / when do you expect this risk to materialise?</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDatePicker
                                                        disabled={(risk.RiskIsOngoing) || (risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0)}
                                                        value={DateService.removeTimezoneOffset(risk.RiskProximity)}
                                                        onSelectDate={v => changeHandlers.changeDatePicker(v, 'RiskProximity')}
                                                        minDate={risk.ID === 0 && new Date()}
                                                        errorMessage={errors.RiskProximity}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 5th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Which Departmental Objective will this most impact?</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions(lookupData.DepartmentalObjectives)}
                                                        selectedKey={risk.DepartmentalObjectiveID}
                                                        onChange={(_, v) => changeHandlers.changeDropdown(v, 'DepartmentalObjectiveID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 6th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Is the risk ongoing?</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrCheckbox
                                                        label='Yes'
                                                        checked={risk.RiskIsOngoing}
                                                        onChange={(_, isChecked) => this.changeOngoingCheckbox(isChecked, changeHandlers)}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 7th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Is this a project risk?</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrCheckbox
                                                        label='Yes'
                                                        checked={risk.IsProjectRisk}
                                                        onChange={(_, isChecked) => this.changeIsProjectRiskCheckbox(isChecked, changeHandlers)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 8th row */}
                                        {risk.IsProjectRisk && <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Project</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrComboBox
                                                        required={true}
                                                        options={this.getProjectOptions(lookupData.Projects)}
                                                        selectedKey={risk.ProjectID}
                                                        onChange={(_, v) => changeHandlers.changeDropdown(v, 'ProjectID')}
                                                        errorMessage={errors.ProjectID}
                                                    />
                                                </div>
                                            </div>
                                        </div>}


                                    </div>

                                </div>

                                {/* Ownership and Roles Section */}
                                <div>
                                    <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Ownership and Roles</div>
                                        </div>
                                    </div>
                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Register Owner Type</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions([
                                                            { ID: 0, Title: orbConfig.OrgTypeLabel },
                                                            { ID: 1, Title: orbConfig.OrgL1Label },
                                                            { ID: 2, Title: orbConfig.OrgL2Label },
                                                            { ID: 3, Title: orbConfig.OrgL3Label },
                                                        ])}
                                                        selectedKey={risk.RiskRegisterID}
                                                        onChange={(_, o) => changeHandlers.changeDropdown(o, 'RiskRegisterID')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        {/* 2nd row */}
                                        {risk.RiskRegisterID > 0 && <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}>
                                                            {risk.RiskRegisterID === 1 && <span>{`Register Owner ${orbConfig.OrgL1Label}`}</span>}
                                                            {risk.RiskRegisterID === 2 && <span>{`Register Owner ${orbConfig.OrgL2Label}`}</span>}
                                                            {risk.RiskRegisterID === 3 && <span>{`Register Owner ${orbConfig.OrgL3Label}`}</span>}
                                                        </div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    {risk.RiskRegisterID === 1 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                                                            selectedKey={risk.OwnerL1ID}
                                                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL1ID', index)}
                                                        //errorMessage={errors.OwnerL1}
                                                        />
                                                    }
                                                    {risk.RiskRegisterID === 2 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                            selectedKey={risk.OwnerL2ID}
                                                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL2ID', index)}
                                                        //errorMessage={errors.OwnerL2}
                                                        />
                                                    }
                                                    {risk.RiskRegisterID === 3 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                            selectedKey={risk.OwnerL3ID}
                                                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL3ID', index)}
                                                        //errorMessage={errors.OwnerL3}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </div>}

                                        {/* 3rd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Managed By Type</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptions([
                                                            { ID: 2, Title: orbConfig.OrgL2Label },
                                                            { ID: 3, Title: orbConfig.OrgL3Label },
                                                        ])}
                                                        selectedKey={risk.ManagedByTypeID}
                                                        onChange={(_, o) => changeHandlers.changeDropdown(o, 'ManagedByTypeID')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        {/* 4th row */}
                                        {risk.ManagedByTypeID > 1 && <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}>
                                                            {risk.ManagedByTypeID === 2 && <span>{`Managed by ${orbConfig.OrgL2Label}`}</span>}
                                                            {risk.ManagedByTypeID === 3 && <span>{`Managed by ${orbConfig.OrgL3Label}`}</span>}
                                                        </div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    {risk.ManagedByTypeID === 2 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                                            selectedKey={risk.ManagedByL2ID}
                                                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'ManagedByL2ID', index)}
                                                        //errorMessage={errors.ManagedByL2}
                                                        />
                                                    }
                                                    {risk.ManagedByTypeID === 3 &&
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                                            selectedKey={risk.ManagedByL3ID}
                                                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'ManagedByL3ID', index)}
                                                        //errorMessage={errors.ManagedByL3}
                                                        />
                                                    }

                                                </div>
                                            </div>
                                        </div>}

                                        {/* 5th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Risk owner (approver)</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrUserPicker
                                                        required={true}
                                                        users={mappedUsers}
                                                        selectedUsers={risk.RiskOwnerUserID && [risk.RiskOwnerUserID]}
                                                        onChange={u => changeHandlers.changeUserPicker(u, 'RiskOwnerUserID')}
                                                        errorMessage={errors.RiskOwnerUserID}
                                                        {...suggestions}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 6th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Alternative risk approver</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrUserPicker
                                                        users={mappedUsers}
                                                        selectedUsers={risk.ReportApproverUserID && [risk.ReportApproverUserID]}
                                                        onChange={u => changeHandlers.changeUserPicker(u, 'ReportApproverUserID')}
                                                        {...suggestions}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 7th row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Contributors</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrUserPicker
                                                        users={mappedUsers}
                                                        itemLimit={6}
                                                        selectedUsers={risk.Contributors?.map(c => c.ContributorUserID)}
                                                        onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                                                        errorMessage={errors.NormalContributors}
                                                        {...suggestions}
                                                    />
                                                </div>
                                            </div>
                                        </div>



                                    </div>
                                </div>

                                {/* Risk Description Section */}
                                <div>
                                    <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Risk Description</div>
                                        </div>
                                    </div>
                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>

                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Risk Event Description</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrTextField
                                                        maxLength={750}
                                                        rows={6}
                                                        multiline
                                                        value={risk.RiskEventDescription}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'RiskEventDescription')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2nd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Risk Cause Description</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrTextField
                                                        maxLength={750}
                                                        rows={6}
                                                        multiline
                                                        value={risk.RiskCauseDescription}
                                                        onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'RiskCauseDescription')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 3rd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Risk Impact Description</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrTextField
                                                        maxLength={750}
                                                        rows={6}
                                                        multiline
                                                        value={risk.RiskImpactDescription}
                                                        onChange={(_, v) => changeHandlers.changeTextField(v, 'RiskImpactDescription')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Assessment Section */}
                                <div>
                                    <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Assessment</div>
                                        </div>
                                    </div>
                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {unmitigatedProbabilityLabel}
                                                {unmitigatedImpactLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && unmitigatedProbabilityLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptionsFilteredByDate(nowPlusOneMonth, lookupData.RiskProbabilities)}
                                                        selectedKey={risk.UnmitigatedRiskProbabilityID}
                                                        onChange={(_, v) => changeHandlers.changeDropdown(v, 'UnmitigatedRiskProbabilityID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                                {isMobile && unmitigatedImpactLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptionsFilteredByDate(nowPlusOneMonth, lookupData.RiskImpactLevels)}
                                                        selectedKey={risk.UnmitigatedRiskImpactLevelID}
                                                        onChange={(_, v) => changeHandlers.changeDropdown(v, 'UnmitigatedRiskImpactLevelID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2nd row */}
                                        <div className={styles.formField}>
                                            {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                                {targetProbabilityLabel}
                                                {targetImpactLabel}
                                            </div>}
                                            <div className='two-fields-sidebyside-r2'>
                                                {isMobile && targetProbabilityLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptionsFilteredByDate(nowPlusOneMonth, lookupData.RiskProbabilities)}
                                                        selectedKey={risk.TargetRiskProbabilityID}
                                                        onChange={(_, v) => changeHandlers.changeDropdown(v, 'TargetRiskProbabilityID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                                {isMobile && targetImpactLabel}
                                                <div className='two-fields-sidebyside-field'>
                                                    <CrDropdown
                                                        options={LookupService.entitiesToSelectableOptionsFilteredByDate(nowPlusOneMonth, lookupData.RiskImpactLevels)}
                                                        selectedKey={risk.TargetRiskImpactLevelID}
                                                        onChange={(_, v) => changeHandlers.changeDropdown(v, 'TargetRiskImpactLevelID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>


                                    </div>

                                </div>

                                {/* Tracking & Relationships Section */}
                                <div>
                                    <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                        <div style={{ display: 'flex' }}>
                                            <div className={styles.sectionATitle}>Tracking & Relationships</div>
                                        </div>
                                    </div>
                                    <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                        {/* 1st row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Reports Due</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrReportingCyclePicker
                                                        required={true}
                                                        cycle={{ frequency: risk.ReportingFrequency, dueDay: risk.ReportingDueDay, startDate: risk.ReportingStartDate }}
                                                        onChange={cycle => changeHandlers.changeReportingCycle(cycle)}
                                                        errorMessage={errors.ReportingCycle}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2nd row */}
                                        {risk.RiskRegisterID !== RiskRegister.Departmental && <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Linked Risk</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrEntityPicker
                                                        itemLimit={1}
                                                        required={false}
                                                        entities={this.getAvailableRisksForLinking(risk)}
                                                        selectedEntities={risk.LinkedRiskID && [risk.LinkedRiskID]}
                                                        onChange={v => changeHandlers.changeUserPicker(v, 'LinkedRiskID')}
                                                        disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    />
                                                </div>
                                            </div>
                                        </div>}

                                        {/* 3rd row */}
                                        <div className={styles.formField}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                    <div className={styles.flexContainerSectionQuestion}>
                                                        <div className={styles.sectionQuestionCol1}><span>Additional Information</span></div>
                                                        <div className={styles.sectionQuestionCol2}>

                                                            {/* inner flex to line 2 images */}
                                                            <div style={{ display: 'flex' }}>
                                                                {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', marginTop: '5px' }}>
                                                <div style={{ width: '100%' }}>
                                                    <CrCustomFields
                                                        customFieldDfns={filteredCustomFieldDfns}
                                                        selectedItems={LookupService.customFieldsToDropdownWithText(risk.CustomFields)}
                                                        onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 4th row */}
                                        {(formState.FormDataBeforeChanges.EntityStatusID === EntityStatus.Closed
                                            || dataServices.riskPermissionsService.RiskCanBeClosed(userContext.UserEntities, risk, this.state.LastSignedOffRiskUpdate)) &&
                                            <div className={styles.formField}>
                                                <div style={{ display: 'flex' }}>
                                                    <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                        <div className={styles.flexContainerSectionQuestion}>
                                                            <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                                                            <div className={styles.sectionQuestionCol2}>

                                                                {/* inner flex to line 2 images */}
                                                                <div style={{ display: 'flex' }}>
                                                                    {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', marginTop: '5px' }}>
                                                    <div style={{ width: '100%' }}>
                                                        <CrDropdown
                                                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                                            selectedKey={risk.EntityStatusID}
                                                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'EntityStatusID')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>}

                                    </div>

                                </div>

                            </>
                        );
                    }}
                    loadEntity={id => dataServices.corporateRiskService.read(id, true, true)}
                    loadNewEntity={() => new CorporateRisk()}
                    // onAfterLoad={risk => {
                    //     if (risk.DirectorateID)
                    //         this.setState({ FilteredProjects: lookupData.Projects.filter(p => p.DirectorateID === risk.DirectorateID) });
                    // }}
                    loadEntityValidations={() => new RiskValidations()}
                    onValidateEntity={validateEntity}
                    onBeforeSave={risk => { delete risk.Directorate; delete risk.CustomFields; }}
                    onCreate={r => dataServices.corporateRiskService.create(r)}
                    onUpdate={r => dataServices.corporateRiskService.update(r.ID, r)}
                    parentEntities={dataServices.corporateRiskService.parentEntities}
                    childEntities={
                        [
                            {
                                ObjectParentProperty: 'RiskRiskTypes',
                                ParentIdProperty: 'RiskID',
                                ChildIdProperty: 'RiskTypeID',
                                ChildService: dataServices.riskRiskTypeService
                            },
                            {
                                ObjectParentProperty: 'Contributors',
                                ParentIdProperty: 'RiskID',
                                ChildIdProperty: 'ContributorUserID',
                                ChildService: dataServices.contributorService
                            },
                            {
                                ObjectParentProperty: 'CustomFields',
                                ParentIdProperty: 'RiskID',
                                ChildIdProperty: 'CustomFieldDefnID',
                                ChildService: dataServices.customFieldService
                            }
                        ]}
                />

                {this.props.entityId > 0 &&
                    <>
                        <div>
                            <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                                <div style={{ display: 'flex' }}>
                                    <div className={styles.sectionATitle}>Discussion, General Comments and File Attachments</div>
                                </div>
                            </div>
                        </div>
                        <div>

                            <div style={{ minHeight: '120px' }}>
                                <EvidenceList
                                    evidenceFor='Risk'
                                    issueActionID={null}
                                    issueID={null}
                                    incidentActionID={null}
                                    incidentID={null}
                                    riskID={this.props.entityId}
                                    riskMitigationActionID={null}
                                    currentUserId={this.props.currentUserId}
                                    superUserPermission={this.props.superUserPermission}
                                    isViewOnly={false}
                                    filterText={this.state.Evidence_ListFilterText}
                                    onChangeFilterText={this.handleEvidence_ChangeFilterText}
                                    evChangesCounter={1}
                                    {...this.props}
                                    errorHandling={this.props.errorHandling}
                                />
                            </div>

                        </div>
                    </>
                }
            </div >
        );
    }

    private handleEvidence_ChangeFilterText = (value: string): void => {
        this.setState({ Evidence_ListFilterText: value });
    }

    public componentDidMount(): void {
        this.loadLastSignedOffRiskUpdate();
        const { loadLookupData: lld } = this.props.dataContext;
        lld?.customFieldDfns();
        lld?.corporateRisks();
        lld?.departmentalObjectives();
        lld?.groups();
        lld?.directorates();
        lld?.orgLevel3s();
        lld?.entityStatuses();
        lld?.projects();
        lld?.riskImpactLevels();
        lld?.riskProbabilities();
        lld?.riskTypes();
        lld?.userDirectorates();
        lld?.userGroups();
        lld?.userProjects();
        lld?.users.all();
    }

    public componentDidUpdate(prevProps: ISpecificEntityFormProps): void {
        if (prevProps.entityId !== this.props.entityId) {
            this.loadLastSignedOffRiskUpdate();
        }
    }

    private loadLastSignedOffRiskUpdate = async (): Promise<IRiskUpdate[]> => {
        try {
            const ru = await this.props.dataContext.dataServices.corporateRiskUpdateService.readLatestSignedOffUpdate(this.props.entityId);
            this.setState({ LastSignedOffRiskUpdate: ru });
            return [ru];
        } catch (err) {
            this.props.errorHandling?.onError(`Error loading latest risk update`, err.message);
        }
    }

    // private changeDirectorateDropdown = (option: IDropdownOption, changeHandlers: IEntityFormChangeHandlers<IRisk>): void => {
    //     this.setState({ FilteredProjects: this.props.dataContext.lookupData.Projects.filter(p => p.DirectorateID === option.key) });
    //     changeHandlers.changeDropdown(option, 'DirectorateID', null, () => changeHandlers.clearField('ProjectID'));
    // }

    private changeIsProjectRiskCheckbox = (isChecked: boolean, changeHandlers: IEntityFormChangeHandlers<IRisk>): void => {
        changeHandlers.changeCheckbox(isChecked, 'IsProjectRisk', () => isChecked || changeHandlers.clearField('ProjectID'));
    }

    private changeOngoingCheckbox = (isChecked: boolean, changeHandlers: IEntityFormChangeHandlers<IRisk>) => {
        if (isChecked)
            changeHandlers.changeCheckbox(isChecked, 'RiskIsOngoing', () => changeHandlers.clearField('RiskProximity'));
        else
            changeHandlers.changeCheckbox(isChecked, 'RiskIsOngoing');
    }

    private getRegisterOptions(risk: IRisk, lastRiskUpdate: IRiskUpdate, registers: IEntity[], formDataBeforeChanges: IRisk): IChoiceGroupOption[] {
        if (risk.ID === 0) {
            const registerIDs = this.props.dataContext.dataServices.riskPermissionsService.RiskCanBeCreatedInRegisters(this.props.orbUserContext.userContext.UserEntities);
            return LookupService.entitiesToChoiceGroupOptions(registers.filter(r => registerIDs.indexOf(r.ID) !== -1));
        } else {
            const registerIDs = this.props.dataContext.dataServices.riskPermissionsService.RiskCanBeEscalatedToRegisters(this.props.orbUserContext.userContext.UserEntities, risk, lastRiskUpdate);
            registerIDs.push(formDataBeforeChanges.RiskRegisterID);
            return LookupService.entitiesToChoiceGroupOptions(registers.filter(r => registerIDs.indexOf(r.ID) !== -1));
        }
    }

    private getDirectorateOptions(directorates: IDirectorate[], risk: IRisk): IDropdownOption[] {
        const directorateIDs = this.props.dataContext.dataServices.riskPermissionsService.RiskCanBeOwnedByDirectorates(this.props.orbUserContext.userContext.UserEntities, directorates);
        if (risk.DirectorateID) directorateIDs.push(risk.DirectorateID);
        return LookupService.entitiesToSelectableOptions(directorates.filter(d => directorateIDs.indexOf(d.ID) !== -1));
    }

    private getAvailableRisksForLinking(risk: IRisk): IEntity[] {
        // Show risks for the Risk Register level above that for the current Risk
        // Ensure the currently-selected linked risk (if any) is always in the list
        if (risk.RiskRegisterID === RiskRegister.Directorate) {
            return this.props.dataContext.lookupData.CorporateRisks
                .filter(r => r.ID !== risk.LinkedRiskID && r.RiskRegisterID === RiskRegister.Group)
                .concat(this.props.dataContext.lookupData.CorporateRisks.filter(r => r.ID === risk.LinkedRiskID));
        } else if (risk.RiskRegisterID === RiskRegister.Group) {
            return this.props.dataContext.lookupData.CorporateRisks
                .filter(r => r.ID !== risk.LinkedRiskID && r.RiskRegisterID === RiskRegister.Departmental)
                .concat(this.props.dataContext.lookupData.CorporateRisks.filter(r => r.ID === risk.LinkedRiskID));
        }
        return this.props.dataContext.lookupData.CorporateRisks.filter(r => r.ID === risk.LinkedRiskID);
    }

    private getProjectOptions(projects: IProject[]): IDropdownOption[] {
        return LookupService.entitiesToSelectableOptions(projects);
    }

    private isNameDisabled(risk: IRisk): boolean {
        return !this.props.dataContext.dataServices.riskPermissionsService.RiskNameCanBeChanged(this.props.orbUserContext.userContext.UserEntities, risk);
    }

    private isRegisterDisabled(): boolean {
        return !this.props.dataContext.dataServices.riskPermissionsService.RiskRegisterCanBeChanged(this.props.orbUserContext.userContext.UserEntities);
    }

    private isDirectorateDisabled(): boolean {
        return !this.props.dataContext.dataServices.riskPermissionsService.RiskDirectorateCanBeChanged(this.props.orbUserContext.userContext.UserEntities);
    }
}
